import React, { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { observer } from 'mobx-react';
import { rgba } from 'polished';
import Tooltip from '@material-ui/core/Tooltip';
import { TableCell, TableRow } from '@this/src/components/shared/ui/data_displays/table';
import { TextTruncate } from '@this/src/components/shared/ui/data_displays/typography';
import type HotelOrderItemAgodaTodo from '@this/src/domain/arrangement/hotel_order_item_agoda_todo';
import { Link } from '@this/src/components/shared/ui/navigations/link';
import { Button } from '@this/components/shared/ui/inputs/button';
import { Checkbox } from '@this/src/components/shared/ui/inputs/checkbox';
import { Fetcher } from '@this/src/util';
import LogModal from '../../../log_modal';

interface Props {
  todo: HotelOrderItemAgodaTodo;
  checked: boolean;
  showDescription: boolean;
  onInventory: () => void;
  onToggle: (checked: boolean) => void;
}

const display = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === '' ? '─' : value;

const peopleLabel = (value: number | null | undefined) => (value == null ? '─' : `${value}人`);

const breakfastLabel = (value: boolean | null | undefined) => {
  if (value == null) return '─';
  return value ? 'あり' : 'なし';
};

const ItemsTableRow = observer(({ todo, checked, showDescription, onInventory, onToggle }: Props) => {
  const [showLogModal, setShowLogModal] = useState(false);
  const styles = useMemo(() => getStyles(), []);
  const rowStyle = { background: todo.isDiff || !todo.orderItemId ? rgba('red', 0.3) : 'auto' };
  const pink = (key: string): CSSProperties | undefined =>
    todo.diff[key] ? { backgroundColor: 'pink' } : undefined;

  const handleInventory = async () => {
    await Fetcher.put(
      `/arrangement/auto_reservation_inventory/agoda_hotel/items/${todo.orderItemId}/inventory`,
      {}
    );
    onInventory();
  };

  const handleCancelInventory = async () => {
    await Fetcher.put(
      `/arrangement/auto_reservation_inventory/agoda_hotel/items/${todo.orderItemId}/cancel_inventory`,
      {}
    );
    onInventory();
  };

  return (
    <>
      <TableRow style={rowStyle}>
        <TableCell rowSpan={2}>
          <Checkbox
            checked={checked}
            disabled={!todo.orderItemId || Boolean(todo.inventoriedAt)}
            onChange={e => onToggle(e.target.checked)}
          />
        </TableCell>
        <TableCell>
          <span style={styles.label}>OrderItem</span>
        </TableCell>
        <TableCell style={pink('reservation_number')}>{display(todo.fromOrderItem.reservationNumber)}</TableCell>
        <TableCell style={pink('people_num')}>{peopleLabel(todo.fromOrderItem.peopleNum)}</TableCell>
        <TableCell style={pink('hotel_name')}>{display(todo.fromOrderItem.hotelName)}</TableCell>
        <TableCell style={pink('hotel_address')}>{display(todo.fromOrderItem.hotelAddress)}</TableCell>
        <TableCell style={pink('room_type')}>{display(todo.fromOrderItem.roomType)}</TableCell>
        <TableCell style={pink('plan_name')}>{display(todo.fromOrderItem.planName)}</TableCell>
        <TableCell>{todo.showMultipleHotelPlans ? '○' : ''}</TableCell>
        <TableCell>{todo.userCheckerStr()}</TableCell>
        <TableCell style={pink('breakfast')}>{breakfastLabel(todo.fromOrderItem.breakfast)}</TableCell>
        <TableCell style={pink('checkin_date')}>{display(todo.fromOrderItem.checkinDate)}</TableCell>
        <TableCell style={pink('checkout_date')}>{display(todo.fromOrderItem.checkoutDate)}</TableCell>
        <TableCell style={pink('traveler_name')}>{display(todo.fromOrderItem.travelerName)}</TableCell>
        <TableCell>─</TableCell>
        <TableCell>─</TableCell>
        <TableCell rowSpan={2} style={pink('original_price')}>
          <div>代金合計: {todo.price?.toLocaleString() ?? '─'}円</div>
          <div>卸値: {todo.originalPrice?.toLocaleString() ?? '─'}円</div>
          <div>
            Agoda側料金:{' '}
            {todo.agodaPriceUnconvertible ? '換算不可' : `${todo.agodaPrice?.toLocaleString() ?? '─'}円`}
          </div>
        </TableCell>
        <TableCell rowSpan={2}>{display(todo.suppliedItemName)}</TableCell>
        <TableCell rowSpan={2}>{todo.orderItemId ? (todo.isInventoried ? '済' : '未') : '─'}</TableCell>
        <TableCell rowSpan={2}>{display(todo.inventoriedBy)}</TableCell>
        <TableCell rowSpan={2}>{display(todo.inventoriedAt)}</TableCell>
        <TableCell rowSpan={2} nowrap={!showDescription}>
          {todo.tripId && (
            <Link
              href={`/arrangement/virtual_counter?trip_id=${todo.tripId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {todo.tripId}
            </Link>
          )}
        </TableCell>
        <TableCell rowSpan={2} nowrap={!showDescription}>
          {display(todo.traceId)}
        </TableCell>
        <TableCell rowSpan={2}>
          {todo.specialCheckinInstructions && (
            <Tooltip
              interactive
              placement="left"
              title={<div style={styles.specialInstructionsTooltip}>{todo.specialCheckinInstructions}</div>}
            >
              <div style={styles.specialInstructions}>
                <TextTruncate lines={6}>{todo.specialCheckinInstructions}</TextTruncate>
              </div>
            </Tooltip>
          )}
        </TableCell>
        <TableCell rowSpan={2}>
          {todo.lastLogMessage && <span>{todo.lastLogMessage}</span>}
          {todo.lastLogMessageArranger && <span>({todo.lastLogMessageArranger})</span>}
          {todo.orderItemId && (
            <p>
              (<Link onClick={() => setShowLogModal(true)}>ログ</Link>)
            </p>
          )}
        </TableCell>
        <TableCell rowSpan={2}>
          {todo.orderItemId &&
            (todo.inventoriedAt ? (
              <Button size="small" onClick={() => handleCancelInventory()}>
                取消
              </Button>
            ) : (
              <Button size="small" onClick={() => handleInventory()}>
                棚卸完了
              </Button>
            ))}
        </TableCell>
        {showLogModal && todo.traceId && (
          <LogModal traceId={todo.traceId} onClose={() => setShowLogModal(false)} />
        )}
      </TableRow>
      <TableRow style={rowStyle}>
        <TableCell>
          <span style={styles.label}>Agoda</span>
        </TableCell>
        <TableCell style={pink('reservation_number')}>
          {display(todo.fromAgodaReservation.reservationNumber)}
        </TableCell>
        <TableCell style={pink('people_num')}>{peopleLabel(todo.fromAgodaReservation.peopleNum)}</TableCell>
        <TableCell style={pink('hotel_name')}>{display(todo.fromAgodaReservation.hotelName)}</TableCell>
        <TableCell style={pink('hotel_address')}>{display(todo.fromAgodaReservation.hotelAddress)}</TableCell>
        <TableCell style={pink('room_type')}>{display(todo.fromAgodaReservation.roomType)}</TableCell>
        <TableCell style={pink('plan_name')}>{display(todo.fromAgodaReservation.planName)}</TableCell>
        <TableCell />
        <TableCell />
        <TableCell style={pink('breakfast')}>{breakfastLabel(todo.fromAgodaReservation.breakfast)}</TableCell>
        <TableCell style={pink('checkin_date')}>{display(todo.fromAgodaReservation.checkinDate)}</TableCell>
        <TableCell style={pink('checkout_date')}>{display(todo.fromAgodaReservation.checkoutDate)}</TableCell>
        <TableCell style={pink('traveler_name')}>{display(todo.fromAgodaReservation.travelerName)}</TableCell>
        <TableCell>{display(todo.fromAgodaReservation.status)}</TableCell>
        <TableCell>
          {todo.fromAgodaReservation.selfServiceUrl ? (
            <Link
              href={todo.fromAgodaReservation.selfServiceUrl}
              isExternal
              target="_blank"
              rel="noopener noreferrer"
            >
              開く
            </Link>
          ) : (
            '─'
          )}
        </TableCell>
      </TableRow>
    </>
  );
});

const getStyles = () => ({
  label: {
    fontSize: '0.8em',
    fontWeight: 'bold'
  } as CSSProperties,
  specialInstructions: {
    fontSize: '0.85em',
    maxWidth: 280,
    whiteSpace: 'pre-wrap'
  } as CSSProperties,
  specialInstructionsTooltip: {
    whiteSpace: 'pre-wrap',
    maxWidth: 400,
    overflow: 'visible',
    fontSize: 12
  } as CSSProperties
});

export default ItemsTableRow;
